import { View, Text, StyleSheet } from '@react-pdf/renderer'
import { Lesson } from '@all-for-one/shared-types'

/**
 * Shared primitives for the lesson PDF exports: the style sheet and the
 * page footer, both used identically by all 3 document types (draft,
 * answer key, worksheet). See
 * docs/BLUEPRINT-2026-09-30-pdf-documents-decomposition.md.
 */

export interface PdfBaseProps {
    lesson: Lesson
    assignedClassName?: string
    gradeBand?: string
    subject?: string
    logoDataUrl: string
    downloadDate: string
}

export const S = StyleSheet.create({
    page: {
        fontFamily: 'Helvetica',
        fontSize: 11,
        color: '#222222',
        paddingTop: 72,
        paddingBottom: 54,
        paddingLeft: 54,
        paddingRight: 54,
    },
    footer: {
        position: 'absolute',
        bottom: 20,
        left: 54,
        right: 54,
        flexDirection: 'row',
        justifyContent: 'space-between',
        fontSize: 8,
        color: '#999999',
    },
    // Document header (draft / answer key)
    docHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottomWidth: 2,
        borderBottomColor: '#333333',
        borderBottomStyle: 'solid',
        paddingBottom: 8,
        marginBottom: 14,
    },
    headerLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    logo: { height: 36 },
    lessonName: { fontFamily: 'Helvetica-Bold', fontSize: 10 },
    headerDetail: { fontSize: 8, color: '#666666', marginTop: 1 },
    badgeDraft: {
        fontSize: 11,
        fontFamily: 'Helvetica-Bold',
        paddingVertical: 5,
        paddingHorizontal: 9,
        backgroundColor: '#fef3cd',
        color: '#856404',
        borderRadius: 4,
    },
    badgeAnswerKey: {
        fontSize: 11,
        fontFamily: 'Helvetica-Bold',
        paddingVertical: 5,
        paddingHorizontal: 9,
        backgroundColor: '#e2e3f1',
        color: '#12247c',
        borderRadius: 4,
    },
    // Section / level headers
    sectionHeader: {
        fontSize: 13,
        fontFamily: 'Helvetica-Bold',
        marginTop: 18,
        marginBottom: 10,
        paddingBottom: 3,
        borderBottomWidth: 1,
        borderBottomColor: '#999999',
        borderBottomStyle: 'solid',
    },
    levelHeader: {
        fontSize: 11,
        fontFamily: 'Helvetica-BoldOblique',
        color: '#444444',
        marginTop: 8,
        marginBottom: 6,
    },
    // Questions (draft / answer key)
    question: { marginBottom: 14 },
    // Questions (worksheet — doubled spacing)
    questionWs: { marginBottom: 48 },
    questionRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 3,
        marginBottom: 4,
    },
    questionLabel: { fontFamily: 'Courier-Bold', fontSize: 10 },
    questionText: { fontSize: 11, flexShrink: 1 },
    answerText: {
        fontFamily: 'Helvetica-Bold',
        color: '#12247c',
        marginTop: 3,
        marginLeft: 20,
        fontSize: 11,
    },
    slideNote: {
        fontSize: 8,
        color: '#888888',
        fontFamily: 'Helvetica-Oblique',
        marginBottom: 2,
    },
    questionImage: {
        alignSelf: 'center',
        maxWidth: '65%',
        maxHeight: 140,
        objectFit: 'contain',
        marginVertical: 6,
    },
    // Multiple choice — horizontal for short options, vertical for long
    mcOptionsRow: {
        flexDirection: 'row',
        gap: 8,
        marginTop: 6,
    },
    mcOptionsCol: {
        flexDirection: 'column',
        gap: 4,
        marginTop: 6,
        marginLeft: 16,
    },
    mcOptionFlex: { flex: 1 },
    mcOptionFull: {},
    mcOptionImage: {
        alignSelf: 'center',
        maxWidth: '100%',
        maxHeight: 60,
        objectFit: 'contain',
        marginBottom: 4,
    },
    mcOptionRow: {
        flexDirection: 'row',
        gap: 2,
        justifyContent: 'flex-start',
    },
    mcLetter: { fontFamily: 'Helvetica-Bold', minWidth: 14 },
    // Fill blank
    fillBlankRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        marginTop: 4,
        gap: 2,
    },
    blankLine: {
        width: 169,   // 225px × (72pt/96dpi) ≈ 169pt
        borderBottomWidth: 1.5,
        borderBottomColor: '#333333',
        borderBottomStyle: 'solid',
        marginHorizontal: 3,
        height: 14,
    },
    // Short answer
    shortAnswerLines: { marginTop: 6, marginLeft: 16, gap: 2 },
    ruledLine: {
        borderBottomWidth: 1,
        borderBottomColor: '#999999',
        borderBottomStyle: 'solid',
        height: 18,
    },
    // Directions box (worksheets)
    directions: {
        backgroundColor: '#f8f9fa',
        borderWidth: 1,
        borderColor: '#dee2e6',
        borderStyle: 'solid',
        borderRadius: 3,
        padding: 8,
        marginBottom: 10,
        marginTop: 4,
    },
    directionLine: { fontSize: 9, color: '#555555', marginVertical: 1 },
    // Worksheet header
    wsTopRow: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginBottom: 16,
    },
    wsNameRow: { flexDirection: 'row', alignItems: 'flex-end', gap: 4 },
    wsNameLabel: { fontSize: 11 },
    wsNameBlank: {
        width: 180,
        borderBottomWidth: 1,
        borderBottomColor: '#333333',
        borderBottomStyle: 'solid',
        height: 14,
    },
    wsDocHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        borderBottomWidth: 2,
        borderBottomColor: '#333333',
        borderBottomStyle: 'solid',
        paddingBottom: 8,
        marginBottom: 10,
    },
})

export const PdfFooter = ({ lessonName, downloadDate, levelLetter }: { lessonName: string; downloadDate: string; levelLetter?: string }) => (
    <View style={S.footer} fixed>
        <Text>All for One · {lessonName}{levelLetter ? ` · ${levelLetter}` : ''} · {downloadDate}</Text>
        <Text render={({ pageNumber, totalPages }: { pageNumber: number; totalPages: number }) =>
            `Page ${pageNumber} of ${totalPages}`
        } />
    </View>
)
